import {isDevMode} from '@angular/core';

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const WHITE: Rgb = {r: 255, g: 255, b: 255};
const BLACK: Rgb = {r: 0, g: 0, b: 0};

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function channelToLinear(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

export function hexToRgb(hex: string): Rgb | null {
  const normalized = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

export function rgbToHex({r, g, b}: Rgb): string {
  const toHex = (channel: number) =>
    clampChannel(channel).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function relativeLuminance({r, g, b}: Rgb): number {
  const red = channelToLinear(r);
  const green = channelToLinear(g);
  const blue = channelToLinear(b);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

export function contrastRatio(
  foregroundHex: string,
  backgroundHex: string,
): number {
  const foreground = hexToRgb(foregroundHex);
  const background = hexToRgb(backgroundHex);
  if (!foreground || !background) {
    return 1;
  }

  const lighter = Math.max(
    relativeLuminance(foreground),
    relativeLuminance(background),
  );
  const darker = Math.min(
    relativeLuminance(foreground),
    relativeLuminance(background),
  );
  return (lighter + 0.05) / (darker + 0.05);
}

function mixRgb(from: Rgb, to: Rgb, amount: number): Rgb {
  const ratio = Math.max(0, Math.min(1, amount));
  return {
    r: from.r + (to.r - from.r) * ratio,
    g: from.g + (to.g - from.g) * ratio,
    b: from.b + (to.b - from.b) * ratio,
  };
}

/**
 * Returns a foreground color guaranteed to meet the minimum contrast ratio
 * against the provided background color.
 */
export function ensureReadable(
  foregroundHex: string,
  backgroundHex: string,
  minRatio = 4.5,
): string {
  const foreground = hexToRgb(foregroundHex);
  const background = hexToRgb(backgroundHex);
  if (!foreground || !background) {
    return foregroundHex;
  }

  if (contrastRatio(foregroundHex, backgroundHex) >= minRatio) {
    return foregroundHex;
  }

  const target = relativeLuminance(background) > 0.5 ? BLACK : WHITE;
  const targetHex = rgbToHex(target);

  for (let step = 1; step <= 20; step++) {
    const mixed = rgbToHex(mixRgb(foreground, target, step / 20));
    if (contrastRatio(mixed, backgroundHex) >= minRatio) {
      if (isDevMode() && mixed !== foregroundHex) {
        console.warn(
          `[theme] Tenant color adjusted for contrast: ${foregroundHex} -> ${mixed}`,
        );
      }
      return mixed;
    }
  }

  if (isDevMode() && targetHex !== foregroundHex) {
    console.warn(
      `[theme] Tenant color adjusted for contrast: ${foregroundHex} -> ${targetHex}`,
    );
  }
  return targetHex;
}

/** Best foreground (black or white) for text/icons on a solid brand fill. */
export function pickOnPrimaryText(backgroundHex: string): string {
  const white = '#ffffff';
  const black = '#000000';
  if (!hexToRgb(backgroundHex)) {
    return white;
  }
  return contrastRatio(white, backgroundHex) >=
    contrastRatio(black, backgroundHex)
    ? white
    : black;
}

/** Gradient stops for hero cards that stay legible with on-primary text. */
export function buildBrandHeroGradient(primary: string): {
  from: string;
  to: string;
} {
  const rgb = hexToRgb(primary);
  const deep = '#1e3a5f';
  if (!rgb) {
    return {from: primary, to: `color-mix(in srgb, ${primary} 70%, ${deep})`};
  }

  if (relativeLuminance(rgb) > 0.55) {
    return {
      from: `color-mix(in srgb, ${primary} 35%, ${deep})`,
      to: `color-mix(in srgb, ${primary} 15%, ${deep})`,
    };
  }

  return {
    from: primary,
    to: `color-mix(in srgb, ${primary} 70%, ${deep})`,
  };
}
